import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import { useCafeWizardSlots } from '../../../../queries/cafeWizard';
import { ScopeNotice, ScopeOptions } from './ScopeChoice';

const SLOTS = [
  { k: 'now', ic: '▶️' },
  { k: 'morn', ic: '🌅', slotKey: 'vibes_morning' },
  { k: 'noon', ic: '☀️', slotKey: 'vibes_afternoon' },
  { k: 'eve', ic: '🌆', slotKey: 'vibes_evening' },
];

/**
 * "أين تحفظه؟" — ported from .claude/fastcreate-prototype.html's stepSave().
 *
 * ⚠️ REAL OCCUPANCY, NOT A GUESS. GET /cafe/wizard/slots reads
 * venue_show_slots live — a blank "الصباح" row here would be exactly the
 * silent-control class this project keeps removing (the same fault the dead
 * Match Scheduler "Screens" field and the silent Source step had).
 *
 * ⚠️ PER DEVICE, NOT VENUE-WIDE (2026-09-05). Occupancy became per-device
 * underneath when venue_show_slots did — a venue-wide "مشغول" here would now
 * be actively WRONG the moment two selected screens disagree (occupied on
 * one, free on another). deviceIds is passed in from the wizard's own
 * Screens-step selection and threaded straight into the occupancy query, and
 * a 'mixed' state is rendered EXPLICITLY (its own tag + a per-screen
 * breakdown line) — never averaged into a single busy/free answer and never
 * silently picking one screen's truth over another's.
 *
 * Props: value:'now'|'morn'|'noon'|'eve'|null, onChange:(slot)=>void,
 *        deviceIds:string[] — the devices this build will target
 */
export default function SaveStep({ value, onChange, deviceIds = [], scope = null, onScopeChange = () => {} }) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCafeWizardSlots(deviceIds, true);

  const occupancy = (slotKey) => data?.[slotKey] ?? null;

  const chosen = SLOTS.find((s) => s.k === value);
  const chosenOccupancy = chosen?.slotKey ? occupancy(chosen.slotKey) : null;
  // the slot's show is ALSO used by screens outside this selection: say so now and let the person choose BEFORE saving
  const shared = chosenOccupancy?.shared ?? null;
  const slotTitle = chosen ? t(`cafe_wizard.slot_${chosen.k}_title`) : '';
  // on a phone the notice sits below the slot list: bring it (and the two options) into view when a shared slot is chosen
  const sharedRef = useRef(null);
  useEffect(() => {
    if (shared && sharedRef.current && typeof sharedRef.current.scrollIntoView === 'function') sharedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [shared?.campaign?.id, value]); // eslint-disable-line react-hooks/exhaustive-deps

  const mixedBreakdown = (occ) => (occ?.devices || [])
    .map((d) => (d.occupied
      ? t('cafe_wizard.slot_mixed_row_busy', { screen: d.device_name, name: d.name })
      : t('cafe_wizard.slot_mixed_row_free', { screen: d.device_name })))
    .join(' · ');

  return (
    <div>
      <div className="fc-p-t">{t('cafe_wizard.save_title')}</div>
      <div className="fc-p-s">{t('cafe_wizard.save_subtitle')}</div>

      {isLoading ? (
        <div className="d-flex flex-column" style={{ gap: 9 }}>
          <Skeleton height="58px" borderRadius="11px" />
          <Skeleton height="58px" borderRadius="11px" />
          <Skeleton height="58px" borderRadius="11px" />
          <Skeleton height="58px" borderRadius="11px" />
        </div>
      ) : isError ? (
        <div className="fc-error">{t('cafe_wizard.slots_load_error')}</div>
      ) : (
        SLOTS.map((sl) => {
          const occ = sl.slotKey ? occupancy(sl.slotKey) : null;
          const state = occ?.state ?? null;
          return (
            <button
              key={sl.k} type="button" className="fc-row"
              aria-pressed={value === sl.k}
              onClick={() => onChange(sl.k)}
            >
              <span className="ic">{sl.ic}</span>
              <span className="b">
                <span className="t">{t(`cafe_wizard.slot_${sl.k}_title`)}</span><br />
                <span className="s">{t(`cafe_wizard.slot_${sl.k}_desc`)}</span>
                {state === 'mixed' && (
                  <>
                    <br />
                    <span className="s fc-slot-breakdown">{mixedBreakdown(occ)}</span>
                  </>
                )}
              </span>
              {sl.k !== 'now' && state && state !== 'unknown' && (
                state === 'mixed'
                  ? <span className="tag mixed">{t('cafe_wizard.slot_mixed')}</span>
                  : state === 'busy'
                    ? <span className="tag">{t('cafe_wizard.slot_busy', { name: occ.name })}</span>
                    : <span className="tag free">{t('cafe_wizard.slot_free')}</span>
              )}
            </button>
          );
        })
      )}

      {chosenOccupancy?.state === 'mixed' && (
        <div className="fc-warn">{t('cafe_wizard.slot_replace_warn_mixed', { detail: mixedBreakdown(chosenOccupancy) })}</div>
      )}
      {shared && (
        <div ref={sharedRef}>
          <ScopeNotice shared={shared} slotName={slotTitle} />
          <ScopeOptions shared={shared} slotName={slotTitle} value={scope} onChange={onScopeChange} />
          <div className="fc-inote">{t('cafe_wizard.scope_pick_hint')}</div>
        </div>
      )}
      {!shared && chosenOccupancy?.state === 'busy' && (
        <div className="fc-warn" dangerouslySetInnerHTML={{
          __html: t('cafe_wizard.slot_replace_warn', {
            busy: chosenOccupancy.name,
            slot: t(`cafe_wizard.slot_${chosen.k}_title`),
          }),
        }} />
      )}

      {/* ⚠️ ONE LINE, ALWAYS VISIBLE ON THIS STEP — a re-run replaces a
          slot's content rather than adding to it, so a hand edit to its
          playlist would be lost. Not conditional on which slot is picked:
          true for morning/afternoon/evening alike. */}
      <div className="fc-inote">{t('cafe_wizard.save_replace_content_note')}</div>
    </div>
  );
}
